import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

export const getOrders = createAsyncThunk(
    "order/getOrders",
    async (data, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams({
                type: String(data.activetab ?? '').trim(),
                page: String(data.page ?? 1).trim(),
                perPage: String(data.perPage ?? 10).trim(),
                search: String(data.search ?? '').trim(),
                filter: String(data.filter ?? '').trim(),
                sort_order: String(data.sort_order ?? '').trim(),
                dashboard_key: String(data.dashboard_key ?? '').trim(),
                start_date: String(data.start_date ?? '').trim(),
                end_date: String(data.end_date ?? '').trim(),
            });

            const response = await api.get(`admin/get-all-order?${params.toString()}`);

            return {
                rfqData: response.data,
                rfqType: data.filter
            };
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to get order");
        }
    }
);

export const getOrderCount = createAsyncThunk(
    "order/getOrderCount",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/get-order-count`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load order count");
        }
    }
);

export const fetchTimeLine = createAsyncThunk(
    "order/fetchTimeLine",
    async (order_id, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/get-order-timeline/${order_id}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load timeline");
        }
    }
);

export const fetchCRManager = createAsyncThunk(
    "order/fetchCRManager",
    async (office_id, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/get-city-reps-by-office/${office_id}`);
            const data = response.data.map(row => ({ value: row.id, label: row.first_name }));
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load CR manager");
        }
    }
);

export const fetchCRTeamMember = createAsyncThunk(
    "order/fetchCRTeamMember",
    async (rfq_id, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/get-city-rep-members/${rfq_id}`);
            const data = response.data.map(row => ({ value: row.user_id, label: row.name + " (Orders : " + row?.orders + ")" }));
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load CR member");
        }
    }
);

export const assignOrderToCRManager = createAsyncThunk(
    "order/assignOrderToCRManager",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post("admin/assign-orders-to-cr", data);

            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || `Failed to assign order`);
        }
    }
);

export const assignOrderToCRMember = createAsyncThunk(
    "order/assignOrderToCRMember",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/assign-order-to-cr-members/${data?.order_id}`, data);

            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || `Failed to assign order to member`);
        }
    }
);

export const getRfqSummary = createAsyncThunk(
    "order/getRfqSummary",
    async (rfq_id, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/rfq-summary/${rfq_id}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load rfq summary.");
        }
    }
);

export const fetchOrderItems = createAsyncThunk(
    "order/fetchOrderItems",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/list-product-vendors/${data?.rfq_id}?search=${data?.search || ''}&sort=${data?.sort_order || ''}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load order items");
        }
    }
);

export const getOrderById = createAsyncThunk(
    "order/getOrderById",
    async (order_id, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/get-order-details/${order_id}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to get order details");
        }
    }
);

export const fetchRefundRequest = createAsyncThunk(
    "order/fetchRefundRequest",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/refund-request-list/${data.order_id}?filter=${data.filter}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load refund request");
        }
    }
);
export const fetchOrderRfq = createAsyncThunk(
    "order/fetchOrderRfq",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/quotation-list/${data?.rfq_id}?filter=${data?.filter}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load rfq");
        }
    }
);

export const fetchOrderPayments = createAsyncThunk(
    "order/fetchOrderPayments",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/payment-details/${data?.order_id}?filter=${data?.filter}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load payment");
        }
    }
);

export const approveRejectRefundRequest = createAsyncThunk(
    "order/approveRejectRefundRequest",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post("admin/approve-refund-request", data);

            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || `Failed to approve/reject refund request.`);
        }
    }
);

export const fetchPendingOrders = createAsyncThunk(
    "order/fetchPendingOrders",
    async (data, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("user_id", data.user_id);
            const response = await api.post("admin/get-pending-orders", formData, {
                headers: {
                    "Content-Type": "multipart/form-data", // Ensure backend sees this as form data
                },
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load pending orders");
        }
    }
);

export const downloadRefundRequest = createAsyncThunk(
    "order/downloadRefundRequest",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/generate-refund-req-pdf/${data.id}`);

            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || `Failed to download refund request.`);
        }
    }
);

export const sendOrderToCRManager = createAsyncThunk(
    "order/sendOrderToCRManager",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/send-to-city-rep`, data);

            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || `Failed to send order.`);
        }
    }
);


export const crApproveRejectOrder = createAsyncThunk(
    "order/crApproveRejectOrder",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/approve-city-rep/${data.order_id}`, data);

            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || `Failed to approve/reject order.`);
        }
    }
);


export const fetchAssignedMembers = createAsyncThunk(
    "order/fetchAssignedMembers",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/assignments/rfq/${data?.rfq_id}/order/${data?.order_id}?search=${data?.search}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to get");
        }
    }
);

export const deleteOrders = createAsyncThunk(
    "order/deleteOrders",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/delete-bulk-order`, data);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to delete");
        }
    }
);

export const updateCancellationStatus = createAsyncThunk(
    "order/updateCancellationStatus",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/cancel-order/${data?.id}`, data);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to cancel order");
        }
    }
);

export const uploadRefundPaymentSlip = createAsyncThunk(
    "order/uploadRefundPaymentSlip",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/upload-refund-file`, data, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to upload");
        }
    }
);

export const approveOrderByFinance = createAsyncThunk(
    "order/approveOrderByFinance",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/approve-finance/${data?.order_id}`, data);
            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to approve order.");
        }
    }
);

export const getAllPayments = createAsyncThunk(
    "order/getAllPayments",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/list-all-payments?page=${data?.page}&search=${data?.search}&filter=${data?.filter}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to get payments history.");
        }
    }
);

export const downloadAllInvoicePDF = createAsyncThunk(
    "order/downloadAllInvoicePDF",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/generate-bulk-receipts-pdf`, data);
            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to download.");
        }
    }
);

export const fetchDeliveryPartners = createAsyncThunk(
    "order/fetchDeliveryPartners",
    async ({ country_id, city_id }, { rejectWithValue }) => {
        try {
            const response = await api.get(
                `admin/get-delivery-partners?country_id=${country_id}&city_id=${city_id}`
            );

            console.log("RAW RESPONSE:", response?.data);

            const list = response?.data || [];

            const data = Array.isArray(list)
                ? list.map((dp) => ({
                    value: String(dp.user_id),
                    label: `${dp.full_name || "N/A"}${Number(dp.reject_order_count) > 0
                        ? ` (Reject Count: ${dp.reject_order_count})`
                        : ` (Reject Count: 0)`
                        }`,
                }))
                : [];

            console.log("MAPPED:", data);

            return data;
        } catch (err) {
            console.log("ERROR:", err);
            return rejectWithValue(
                err.response?.data || "Failed to fetch delivery partners"
            );
        }
    }
);

export const assignOrderToDeliveryPartner = createAsyncThunk(
    "order/assignOrderToDeliveryPartner",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await api.post(
                "admin/assign-to-delivery-partner",
                [payload]
            );

            console.log("ASSIGN RESPONSE:", response.data);

            return response.data;
        } catch (err) {
            console.log("ASSIGN ERROR:", err);
            return rejectWithValue(
                err.response?.data || "Failed to assign delivery partner"
            );
        }
    }
);

export const assignMultipleOrderItemToDeliveryPartner = createAsyncThunk(
    "order/assignMultipleOrderItemToDeliveryPartner",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await api.post("admin/bulk-assign-to-delivery-partner", payload);
            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to assign delivery partner"
            );
        }
    }
);

export const updateDeliveryStatus = createAsyncThunk(
    "order/updateDeliveryStatus",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/mark-order-completed`, data);
            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to update delivery status."
            );
        }
    }
);

export const updateVendorAssigmentApprovel = createAsyncThunk(
    "order/updateVendorAssigmentApprovel",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/update-is-admin-approved`, data);

            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || `Failed to approve/reject vendor.`);
        }
    }
);

export const getCompletedOrders = createAsyncThunk(
    "order/getCompletedOrders",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.get(
                `admin/get-completed-orders?type=${data.activetab}&page=${data.page}&perPage=${data.perPage}&search=${data.search}&sort_by=${data.sort_by || 'id'}&sort_order=${data.sort_order}&dashboard_key=${data.dashboard_key || ''}`
            );

            // console.log("API RESPONSE:", response);
            // console.log("RESPONSE DATA:", response.data);
            // console.log("RESPONSE DATA.DATA:", response.data.data);

            return {
                rfqData: response.data,
                rfqType: 'completed_orders'
            };

        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to get completed orders"
            );
        }
    }
);

export const cancelOrderByAdmin = createAsyncThunk(
    "order/cancelOrderByAdmin",
    async (data, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("order_id", data.order_id);
            formData.append("reason", data.reason);
            const response = await api.post(`admin/cancel-order-by-admin`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to cancel order.");
        }
    }
);

export const uploadTaxInvoice = createAsyncThunk(
    "order/uploadTaxInvoice",
    async (data, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("payment_detail_id", data.payment_detail_id);
            formData.append("uploaded_tax_invoice", data.uploaded_tax_invoice);

            const response = await api.post("admin/upload-tax-invoice", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to upload tax invoice.");
        }
    }
);

export const updatePurchasedStatus = createAsyncThunk(
    "order/updatePurchasedStatus",
    async (data, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            data.rfq_product_ids.forEach(id => {
                formData.append("rfq_product_ids[]", id);
            });
            formData.append("is_purchased", data.is_purchased);

            if (data.purchase_invoice_file) {
                formData.append("purchase_invoice_file", data.purchase_invoice_file);
            }

            const response = await api.post("admin/update-purchased-status", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to update purchased status.");
        }
    }
);

export const approveRejectPurchase = createAsyncThunk(
    "order/approveRejectPurchase",
    async (data, { rejectWithValue }) => {
        try {
            const payload = {
                rfq_product_ids: data.rfq_product_ids,
                action: data.action, // "approve" | "reject"
            };

            if (data.action === "reject") {
                payload.rejection_reason = data.rejection_reason;
            }

            const response = await api.post("admin/approve-reject-purchase", payload);
            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to update approval status.");
        }
    }
);

export const rejectOrderDueToPaymentIssue = createAsyncThunk(
    "order/rejectOrderDueToPaymentIssue",
    async (payload, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("order_id", payload.order_id);
            formData.append("reason", payload.reason);

            const response = await api.post(
                "admin/reject-order-due-to-payment-issue",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            return response;
        } catch (error) {
            return rejectWithValue(
                error?.response?.data || { message: "Something went wrong" }
            );
        }
    }
);

export const sendBackToFinance = createAsyncThunk(
    "order/sendBackToFinance",
    async (payload, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("order_id", payload.order_id);

            const response = await api.post(
                "admin/send-back-to-finance",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            return response;
        } catch (error) {
            return rejectWithValue(
                error?.response?.data || { message: "Something went wrong" }
            );
        }
    }
);

export const revertFinanceApproval = createAsyncThunk(
    'order/revertFinanceApproval',
    async ({ order_ids, rejection_reason }, { rejectWithValue }) => {
        try {
            const formData = new FormData();

            order_ids.forEach((id) => {
                formData.append('order_ids[]', id);
            });

            formData.append('rejection_reason', rejection_reason);

            const response = await api.post(
                `admin/revert-finance-approval`,
                formData
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(
                error?.response?.data || {
                    message: 'Something went wrong. Please try again.'
                }
            );
        }
    }
);

export const fetchPurchaseOrderLogs = createAsyncThunk(
    "order/fetchPurchaseOrderLogs",
    async ({ rfq_id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/purchase-order-logs/${rfq_id}`);

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to fetch purchase order logs."
            );
        }
    }
);

export const fetchFinanceTeamMember = createAsyncThunk(
    "order/fetchFinanceTeamMember",
    async (order_id, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/get-order-manager-member/${order_id}`);
            const data = response.data.map(row => ({
                value: row.user_id ?? row.id,
                label: row.name ?? `${row.first_name || ''} ${row.last_name || ''}`.trim(),
            }));
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to load finance team member");
        }
    }
);

export const assignOrderToFinanceMember = createAsyncThunk(
    "order/assignOrderToFinanceMember",
    async (data, { rejectWithValue }) => {
        try {
            const payload = {
                order_id: data.order_id,
                assigned_to: data.finance_member_id,
            };
            const response = await api.post(`admin/assign-finance-member`, payload);
            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || `Failed to assign order to finance member`);
        }
    }
);

export const actionByFinanceOrder = createAsyncThunk(
    "order/actionByFinanceOrder",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`admin/action-by-finanace-order`, data);
            return response;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to process finance action.");
        }
    }
);


const orderSlice = createSlice({
    name: "order",
    initialState: {
        list_orders: [],
        list_completed_orders: [],
        order_details: null,
        selected: null,
        status: 'idle',
        loading: false,
        customers: [],
        customersInfo: null,
        timeline: null,
        cr_team: [],
        cr_managers: [],
        finance_team: [],
        order_counts: [],
        order_items: [],
        refund_requests: [],
        order_rfq: [],
        order_payments: [],
        assigned_members: [],
        payment_list: {
            paymentlist: {
                data: [],
            },
            dashboard: {},
        },
        hasMore: true,
        pending_orders: [],
        pending_orders_loading: false,
        list_loading: false,
        delivery_partners: [],
        purchase_order_logs: [],
        purchase_order_logs_loading: false,
        payment_card_data: {},
        unread_counts: {},
        unread_type_counts: { normal: 0, custom: 0 },
    },
    reducers: {
        resetStatus(state) {
            state.status = "idle";
            state.loading = false;
            state.list_loading = false
        },
        setEmptyCRTeamMember(state) {
            state.cr_team = [];
        },
        setEmptyFinanceTeamMember(state) {
            state.finance_team = [];
        },
        clearOrders: (state) => {
            state.list_orders = [];
            state.hasMore = true;
        }

    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCRManager.fulfilled, (state, action) => {
                state.cr_managers = action.payload;
            })
            .addCase(fetchCRTeamMember.fulfilled, (state, action) => {
                state.cr_team = action.payload;
            })
            .addCase(fetchTimeLine.fulfilled, (state, action) => {
                state.timeline = action.payload;
            })
            .addCase(fetchOrderItems.pending, (state) => {
                state.list_loading = true;
            })
            .addCase(fetchOrderItems.fulfilled, (state, action) => {
                state.order_items = action.payload;
                state.list_loading = false;
            })
            .addCase(fetchOrderItems.rejected, (state) => {
                state.list_loading = false;
            })
            .addCase(getOrders.pending, (state) => {
                state.list_loading = true;
            })
            .addCase(getOrders.fulfilled, (state, action) => {
                state.list_loading = false;
                const { rfqData, rfqType } = action.payload;
                state.total_count = rfqData?.count;
                const newRfqs = rfqData[rfqType]?.data || [];
                const currentPage = rfqData[rfqType]?.pagination?.current_page || 1;
                if (currentPage === 1) {
                    state.list_orders = newRfqs;
                } else {
                    state.list_orders = [...state.list_orders, ...newRfqs];
                }
                const pagination = rfqData[rfqType]?.pagination;
                if (pagination && pagination.current_page >= pagination.last_page) {
                    state.hasMore = false;
                } else {
                    state.hasMore = true;
                }
                state.unread_counts = rfqData?.unread_counts ?? state.unread_counts;
                state.unread_type_counts = rfqData?.unread_type_counts ?? state.unread_type_counts;
            })
            .addCase(getOrders.rejected, (state) => {
                state.list_loading = false;
            })
            .addCase(getOrderById.pending, (state) => {
                state.list_loading = true;
            })
            .addCase(getOrderById.fulfilled, (state, action) => {
                state.list_loading = false;
                state.order_details = action.payload;
            })
            .addCase(getOrderById.rejected, (state) => {
                state.list_loading = false;
            })
            .addCase(fetchRefundRequest.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchRefundRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.refund_requests = action.payload;
            })
            .addCase(fetchRefundRequest.rejected, (state) => {
                state.loading = false;
            })
            .addCase(fetchOrderRfq.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchOrderRfq.fulfilled, (state, action) => {
                state.loading = false;
                state.order_rfq = action.payload;
            })
            .addCase(fetchOrderRfq.rejected, (state) => {
                state.loading = false;
            })
            .addCase(fetchOrderPayments.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchOrderPayments.fulfilled, (state, action) => {
                state.loading = false;
                state.order_payments = action.payload;
            })
            .addCase(fetchOrderPayments.rejected, (state) => {
                state.loading = false;
            })
            .addCase(assignOrderToCRManager.pending, (state) => {
                state.loading = true;
            })
            .addCase(assignOrderToCRManager.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(assignOrderToCRManager.rejected, (state) => {
                state.loading = false;
            })
            .addCase(assignOrderToCRMember.pending, (state) => {
                state.loading = true;
            })
            .addCase(assignOrderToCRMember.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(assignOrderToCRMember.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getRfqSummary.fulfilled, (state, action) => {
                state.rfq_summary = action.payload;
            })
            .addCase(approveRejectRefundRequest.pending, (state) => {
                state.loading = true;
            })
            .addCase(approveRejectRefundRequest.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(approveRejectRefundRequest.rejected, (state) => {
                state.loading = false;
            })
            .addCase(downloadRefundRequest.pending, (state) => {
                state.loading = true;
            })
            .addCase(downloadRefundRequest.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(downloadRefundRequest.rejected, (state) => {
                state.loading = false;
            })
            .addCase(sendOrderToCRManager.pending, (state) => {
                state.loading = true;
            })
            .addCase(sendOrderToCRManager.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(sendOrderToCRManager.rejected, (state) => {
                state.loading = false;
            })
            .addCase(crApproveRejectOrder.pending, (state) => {
                state.loading = true;
            })
            .addCase(crApproveRejectOrder.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(crApproveRejectOrder.rejected, (state) => {
                state.loading = false;
            })
            .addCase(fetchAssignedMembers.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchAssignedMembers.fulfilled, (state, action) => {
                state.loading = false;
                state.assigned_members = action.payload;
            })
            .addCase(fetchAssignedMembers.rejected, (state) => {
                state.loading = false;
            })
            .addCase(deleteOrders.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteOrders.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(deleteOrders.rejected, (state) => {
                state.loading = false;
            })
            .addCase(uploadRefundPaymentSlip.pending, (state) => {
                state.loading = true;
            })
            .addCase(uploadRefundPaymentSlip.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(uploadRefundPaymentSlip.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getOrderCount.fulfilled, (state, action) => {
                state.order_counts = action.payload;
            })
            .addCase(approveOrderByFinance.pending, (state) => {
                state.loading = true;
            })
            .addCase(approveOrderByFinance.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(approveOrderByFinance.rejected, (state) => {
                state.loading = false;
            })
            .addCase(updateCancellationStatus.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateCancellationStatus.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(updateCancellationStatus.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getAllPayments.pending, (state) => {
                state.list_loading = true;
            })
            .addCase(getAllPayments.fulfilled, (state, action) => {
                state.list_loading = false;
                const newData = action.payload.paymentlist?.data ?? [];
                if (newData?.length === 0) {
                    state.payment_list.paymentlist.data = [];
                } else if (action.payload?.paymentlist?.current_page === 1) {
                    state.payment_list.paymentlist.data = newData;
                } else {
                    state.payment_list.paymentlist.data = [...state.payment_list.paymentlist.data, ...newData];
                }

                state.payment_card_data = action?.payload?.dashboard ?? {};
                state.hasMore = action.payload?.paymentlist?.last_page > action.payload?.paymentlist?.current_page;
            })
            .addCase(getAllPayments.rejected, (state) => {
                state.list_loading = false;
            })
            .addCase(downloadAllInvoicePDF.pending, (state) => {
                state.loading = true;
            })
            .addCase(downloadAllInvoicePDF.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(downloadAllInvoicePDF.rejected, (state) => {
                state.loading = false;
            })
            .addCase(fetchPendingOrders.pending, (state) => {
                state.pending_orders_loading = true;
            })
            .addCase(fetchPendingOrders.fulfilled, (state, action) => {
                state.pending_orders_loading = false;
                state.pending_orders = action.payload || [];
            })
            .addCase(fetchPendingOrders.rejected, (state) => {
                state.pending_orders_loading = false;
                state.pending_orders = [];
            })
            .addCase(fetchDeliveryPartners.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchDeliveryPartners.fulfilled, (state, action) => {
                state.loading = false;
                state.delivery_partners = action.payload;
            })
            .addCase(fetchDeliveryPartners.rejected, (state) => {
                state.loading = false;
            })
            .addCase(assignOrderToDeliveryPartner.pending, (state) => {
                state.loading = true;
            })
            .addCase(assignOrderToDeliveryPartner.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(assignOrderToDeliveryPartner.rejected, (state) => {
                state.loading = false;
            })
            .addCase(assignMultipleOrderItemToDeliveryPartner.pending, (state) => {
                state.loading = true;
            })
            .addCase(assignMultipleOrderItemToDeliveryPartner.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(assignMultipleOrderItemToDeliveryPartner.rejected, (state) => {
                state.loading = false;
            })
            .addCase(updateDeliveryStatus.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateDeliveryStatus.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(updateDeliveryStatus.rejected, (state) => {
                state.loading = false;
            })
            .addCase(updateVendorAssigmentApprovel.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateVendorAssigmentApprovel.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(updateVendorAssigmentApprovel.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getCompletedOrders.pending, (state) => {
                state.list_loading = true;
            })
            .addCase(getCompletedOrders.fulfilled, (state, action) => {
                state.list_loading = false;

                if (!action.payload || !action.payload.rfqData) {
                    return;
                }

                const { rfqData, rfqType } = action.payload;
                state.total_count = rfqData?.count;

                // DIRECT ACCESS
                const orderObject = rfqData[rfqType];

                if (!orderObject) {
                    console.log("orderObject missing", rfqData);
                    return;
                }

                // total count
                state.total_count = {
                    ...state.total_count,
                    [rfqType]: orderObject.pagination?.total || rfqData.count || 0
                };

                const newOrders = orderObject.data || [];
                const currentPage = orderObject.pagination?.current_page || 1;

                if (currentPage === 1) {
                    state.list_completed_orders = newOrders;
                } else {

                    const existingIds = new Set(
                        state.list_completed_orders.map(order => order.id)
                    );

                    const filteredOrders = newOrders.filter(
                        order => !existingIds.has(order.id)
                    );

                    state.list_completed_orders = [
                        ...state.list_completed_orders,
                        ...filteredOrders
                    ];
                }

                state.hasMore =
                    orderObject.pagination?.current_page <
                    orderObject.pagination?.last_page;
            })
            .addCase(cancelOrderByAdmin.pending, (state) => {
                state.loading = true;
            })
            .addCase(cancelOrderByAdmin.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(cancelOrderByAdmin.rejected, (state) => {
                state.loading = false;
            })
            .addCase(uploadTaxInvoice.pending, (state) => {
                state.loading = true;
            })
            .addCase(uploadTaxInvoice.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(uploadTaxInvoice.rejected, (state) => {
                state.loading = false;
            })
            .addCase(updatePurchasedStatus.pending, (state) => {
                state.loading = true;
            })
            .addCase(updatePurchasedStatus.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(updatePurchasedStatus.rejected, (state) => {
                state.loading = false;
            })
            .addCase(approveRejectPurchase.pending, (state) => {
                state.loading = true;
            })
            .addCase(approveRejectPurchase.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(approveRejectPurchase.rejected, (state) => {
                state.loading = false;
            })
            .addCase(rejectOrderDueToPaymentIssue.pending, (state) => {
                state.loading = true;
            })
            .addCase(rejectOrderDueToPaymentIssue.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(rejectOrderDueToPaymentIssue.rejected, (state) => {
                state.loading = false;
            })
            .addCase(sendBackToFinance.pending, (state) => {
                state.loading = true;
            })
            .addCase(sendBackToFinance.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(sendBackToFinance.rejected, (state) => {
                state.loading = false;
            })
            .addCase(revertFinanceApproval.pending, (state) => {
                state.loading = true;
            })
            .addCase(revertFinanceApproval.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(revertFinanceApproval.rejected, (state) => {
                state.loading = false;
            })
            .addCase(fetchPurchaseOrderLogs.pending, (state) => {
                state.purchase_order_logs_loading = true;
            })
            .addCase(fetchPurchaseOrderLogs.fulfilled, (state, action) => {
                state.purchase_order_logs_loading = false;
                state.purchase_order_logs = action.payload?.data || [];
            })
            .addCase(fetchPurchaseOrderLogs.rejected, (state, action) => {
                state.purchase_order_logs_loading = false;
                state.purchase_order_logs = [];
                state.error = action.payload;
            })
            .addCase(fetchFinanceTeamMember.fulfilled, (state, action) => {
                state.finance_team = action.payload;
            })
            .addCase(assignOrderToFinanceMember.pending, (state) => {
                state.loading = true;
            })
            .addCase(assignOrderToFinanceMember.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(assignOrderToFinanceMember.rejected, (state) => {
                state.loading = false;
            })
            .addCase(actionByFinanceOrder.pending, (state) => { state.loading = true; })
            .addCase(actionByFinanceOrder.fulfilled, (state) => { state.loading = false; })
            .addCase(actionByFinanceOrder.rejected, (state) => { state.loading = false; })
    }
});

export const { resetStatus, setEmptyCRTeamMember, clearOrders, setEmptyFinanceTeamMember } = orderSlice.actions;
export default orderSlice.reducer;
